import { EditOutlined, KeyOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Space } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "@/shared/hooks/usePermission";
import { useResetEmployeePassword } from "../hooks/useEmployeeProfile";
import { useUpdateEmployee } from "../hooks/useEmployees";
import type { EmployeeDetail, EmployeeFormValues } from "../types";
import { EmployeeFormModal } from "./EmployeeFormModal";
import { TemporaryPasswordModal } from "./TemporaryPasswordModal";

/**
 * Profil sarlavhasining ikki amali — «Tahrirlash» va «Parolni tiklash»
 * (dizayn kanvasi, `XodimProfili.dc.html` ning `.pactions` bloki).
 *
 * ⚠️⚠️ **Ruxsat bo'lmasa tugma UMUMAN chizilmaydi, `disabled` QILINMAYDI.**
 * Urug'langan `Rahbar` rolida na `employees:update`, na `users:update` bor
 * (`packages/db/src/seed.ts:122-140`) — o'chirilgan tugma rahbarga
 * bajarolmaydigan amalni ko'rsatib turardi, bosilsa esa 403 berardi.
 *
 * ⚠️ **Kanvasdagi uchinchi «⋯» tugmasi ATAYLAB yo'q** — uning ichidagi
 * amallar (arxivlash, rasm almashtirish) hali profil sahifasiga
 * ulanmagan va bo'sh menyu faqat noto'g'ri va'da bo'lardi.
 */
export function EmployeeHeaderActions({ employee }: { employee: EmployeeDetail }) {
	const { t } = useTranslation("employees");
	const { can } = usePermission();
	const [formOpen, setFormOpen] = useState(false);
	const [parol, setParol] = useState<string | null>(null);

	const updateEmployee = useUpdateEmployee();
	const resetPassword = useResetEmployeePassword();

	const tahrirlash = can("employees", "update");
	const tiklash = can("users", "update");

	if (!(tahrirlash || tiklash)) {
		return null;
	}

	// ⚠️ `useUpdateEmployee` `["employees"]` ni invalidatsiya qiladi
	// (`useEmployees.ts:178`), TanStack Query esa kalitni prefiks bo'yicha
	// solishtiradi — `["employees","detail",id]` shu bilan allaqachon
	// qamraladi. Qo'shimcha invalidatsiya faqat ikkinchi refetch berardi.
	const handleSubmit = (values: EmployeeFormValues) => {
		updateEmployee.mutate(
			{
				id: employee.id,
				input: {
					fullName: values.fullName,
					idCode: values.idCode?.trim() ? values.idCode.trim() : null,
					positionId: values.positionId ?? null,
					departmentId: values.departmentId ?? null,
					managerId: values.managerId ?? null,
					hrStatus: values.hrStatus,
				},
			},
			{ onSuccess: () => setFormOpen(false) }
		);
	};

	return (
		<>
			<Space size="small">
				{tahrirlash ? (
					<Button icon={<EditOutlined />} onClick={() => setFormOpen(true)}>
						{t("headerActions.edit")}
					</Button>
				) : null}

				{tiklash ? (
					<Popconfirm
						title={t("headerActions.confirmTitle")}
						description={t("headerActions.confirmDescription")}
						okText={t("headerActions.confirmOk")}
						cancelText={t("headerActions.confirmCancel")}
						// ⚠️ `employee.userId`, `employee.id` EMAS — marshrut
						// `users` resursida (`users.routes.ts:112`).
						onConfirm={() =>
							resetPassword.mutate(employee.userId, { onSuccess: (yangi) => setParol(yangi) })
						}
					>
						<Button icon={<KeyOutlined />} loading={resetPassword.isPending}>
							{t("headerActions.resetPassword")}
						</Button>
					</Popconfirm>
				) : null}
			</Space>

			{tahrirlash ? (
				<EmployeeFormModal
					open={formOpen}
					editing={employee}
					isSubmitting={updateEmployee.isPending}
					onCancel={() => setFormOpen(false)}
					onSubmit={handleSubmit}
				/>
			) : null}

			<TemporaryPasswordModal
				result={parol === null ? null : { employee, temporaryPassword: parol }}
				mode="reset"
				onClose={() => setParol(null)}
			/>
		</>
	);
}
